import React, { useEffect, useMemo, useRef, useState } from 'react';
import { authService, doctorSketchService } from '../services/api';
import './DoctorSketchesPanel.css';

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const emptyUploadForm = {
  doctor_id: '',
  nombre_visible: '',
  descripcion: '',
  file: null,
};

const DoctorSketchesPanel = ({ user }) => {
  const isAdmin = user.rol === 'admin';
  const replaceInputRefs = useRef({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  const [items, setItems] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    ...emptyUploadForm,
    doctor_id: isAdmin ? '' : String(user.id),
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ doctor_id: '', nombre_visible: '', descripcion: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sortedDoctorOptions = useMemo(
    () => doctorOptions
      .filter((doctor) => doctor.rol === 'doctor')
      .sort((left, right) => left.nombre.localeCompare(right.nombre, 'es-MX')),
    [doctorOptions]
  );

  const loadSketches = async () => {
    try {
      setLoading(true);
      const result = await doctorSketchService.list();
      setItems(result.success ? (result.data || []) : []);
    } catch (requestError) {
      setError('No se pudieron cargar los bocetos de doctores.');
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    if (!isAdmin) {
      return;
    }

    try {
      const result = await authService.getDoctors();
      const doctors = result.success ? (result.data || []) : [];
      setDoctorOptions(doctors);
      setUploadForm((current) => ({
        ...current,
        doctor_id: current.doctor_id || String(doctors.find((doctor) => doctor.rol === 'doctor')?.id || ''),
      }));
    } catch (requestError) {
      setDoctorOptions([]);
    }
  };

  useEffect(() => {
    loadSketches();
    loadDoctors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetUploadForm = () => {
    setUploadForm({
      ...emptyUploadForm,
      doctor_id: isAdmin ? (sortedDoctorOptions[0] ? String(sortedDoctorOptions[0].id) : '') : String(user.id),
    });
    setUploadProgress(0);
    const input = document.getElementById('doctor-sketch-upload-input');
    if (input) input.value = '';
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      setError('Selecciona un archivo para subir el boceto.');
      return;
    }

    if (isAdmin && !uploadForm.doctor_id) {
      setError('Selecciona a que doctor pertenece el boceto.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await doctorSketchService.upload({
        file: uploadForm.file,
        doctor_id: isAdmin ? uploadForm.doctor_id : undefined,
        nombre_visible: uploadForm.nombre_visible,
        descripcion: uploadForm.descripcion,
      }, setUploadProgress);
      setSuccess(`"${uploadForm.file.name}" se subio correctamente.`);
      resetUploadForm();
      await loadSketches();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No se pudo subir el boceto.');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleOpen = async (item, download = false) => {
    try {
      setError('');
      await doctorSketchService.open(item.id, item.nombre_original, download);
    } catch (requestError) {
      setError(download ? 'No se pudo descargar el boceto.' : 'No se pudo abrir el boceto.');
    }
  };

  const beginEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      doctor_id: String(item.doctor_id),
      nombre_visible: item.nombre_visible || '',
      descripcion: item.descripcion || '',
    });
    setError('');
    setSuccess('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ doctor_id: '', nombre_visible: '', descripcion: '' });
  };

  const saveEdit = async (itemId) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await doctorSketchService.update(itemId, {
        doctor_id: isAdmin ? editForm.doctor_id : undefined,
        nombre_visible: editForm.nombre_visible,
        descripcion: editForm.descripcion,
      });
      setSuccess('Boceto actualizado.');
      cancelEdit();
      await loadSketches();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No se pudo actualizar el boceto.');
    } finally {
      setSaving(false);
    }
  };

  const handleReplace = async (item, file) => {
    if (!file) return;

    try {
      setReplacingId(item.id);
      setError('');
      setSuccess('');
      await doctorSketchService.replace(item.id, {
        file,
        doctor_id: isAdmin ? item.doctor_id : undefined,
        nombre_visible: item.nombre_visible,
        descripcion: item.descripcion,
      });
      setSuccess(`"${item.nombre_visible}" se reemplazo correctamente.`);
      await loadSketches();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No se pudo reemplazar el archivo del boceto.');
    } finally {
      setReplacingId(null);
      if (replaceInputRefs.current[item.id]) {
        replaceInputRefs.current[item.id].value = '';
      }
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar el boceto "${item.nombre_visible}"?`)) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await doctorSketchService.delete(item.id);
      setSuccess('Boceto eliminado.');
      await loadSketches();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No se pudo eliminar el boceto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="info-card doctor-sketches-panel">
      <div className="doctor-sketches-panel__header">
        <div>
          <h4>Bocetos para Reportes</h4>
          <p>
            {isAdmin
              ? 'Admin puede subir y administrar bocetos de cualquier doctor.'
              : 'Cada doctor solo puede ver y administrar sus propios bocetos.'}
          </p>
        </div>
      </div>

      {error && <div className="doctor-sketches-panel__message doctor-sketches-panel__message--error">{error}</div>}
      {success && <div className="doctor-sketches-panel__message doctor-sketches-panel__message--success">{success}</div>}

      <div className="doctor-sketches-panel__upload">
        {isAdmin && (
          <label className="doctor-sketches-panel__field">
            <span>Doctor</span>
            <select
              value={uploadForm.doctor_id}
              onChange={(event) => setUploadForm((current) => ({ ...current, doctor_id: event.target.value }))}
              disabled={saving}
            >
              <option value="">Selecciona doctor</option>
              {sortedDoctorOptions.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>{doctor.nombre}</option>
              ))}
            </select>
          </label>
        )}

        <label className="doctor-sketches-panel__field">
          <span>Título/Nombre del boceto</span>
          <input
            type="text"
            value={uploadForm.nombre_visible}
            onChange={(event) => setUploadForm((current) => ({ ...current, nombre_visible: event.target.value }))}
            placeholder="Ej: Formato de evolucion quirurgica"
            disabled={saving}
          />
        </label>

        <label className="doctor-sketches-panel__field">
          <span>Descripcion</span>
          <input
            type="text"
            value={uploadForm.descripcion}
            onChange={(event) => setUploadForm((current) => ({ ...current, descripcion: event.target.value }))}
            placeholder="Notas o uso sugerido"
            disabled={saving}
          />
        </label>

        <label className="doctor-sketches-panel__field">
          <span>Archivo</span>
          <input
            id="doctor-sketch-upload-input"
            type="file"
            onChange={(event) => setUploadForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
            disabled={saving}
          />
        </label>

        <button
          type="button"
          className="btn btn-primary doctor-sketches-panel__upload-button"
          onClick={handleUpload}
          disabled={saving || !uploadForm.file}
        >
          {saving ? `Subiendo ${uploadProgress}%` : 'Subir boceto'}
        </button>
      </div>

      {saving && uploadProgress > 0 && (
        <div className="doctor-sketches-panel__progress">
          <div style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {loading ? (
        <p className="cie-status">Cargando bocetos...</p>
      ) : items.length === 0 ? (
        <p className="cie-status">Aun no hay bocetos cargados.</p>
      ) : (
        <div className="doctor-sketches-panel__table-wrap">
          <table className="doctor-sketches-panel__table">
            <thead>
              <tr>
                {isAdmin && <th>Doctor</th>}
                <th>Nombre</th>
                <th>Archivo</th>
                <th>Descripcion</th>
                <th>Tamano</th>
                <th>Actualizado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isEditing = editingId === item.id;
                return (
                  <tr key={item.id}>
                    {isAdmin && (
                      <td>
                        {isEditing ? (
                          <select
                            value={editForm.doctor_id}
                            onChange={(event) => setEditForm((current) => ({ ...current, doctor_id: event.target.value }))}
                            disabled={saving}
                          >
                            {sortedDoctorOptions.map((doctor) => (
                              <option key={doctor.id} value={doctor.id}>{doctor.nombre}</option>
                            ))}
                          </select>
                        ) : (
                          <div>
                            <strong>{item.doctor_nombre}</strong>
                            <small>@{item.doctor_username}</small>
                          </div>
                        )}
                      </td>
                    )}
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.nombre_visible}
                          onChange={(event) => setEditForm((current) => ({ ...current, nombre_visible: event.target.value }))}
                          disabled={saving}
                        />
                      ) : (
                        <strong>{item.nombre_visible}</strong>
                      )}
                    </td>
                    <td>{item.nombre_original}</td>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.descripcion}
                          onChange={(event) => setEditForm((current) => ({ ...current, descripcion: event.target.value }))}
                          disabled={saving}
                        />
                      ) : (
                        item.descripcion || '-'
                      )}
                    </td>
                    <td>{formatFileSize(item.tamano)}</td>
                    <td>{formatDate(item.updated_at)}</td>
                    <td>
                      <div className="doctor-sketches-panel__actions">
                        {isEditing ? (
                          <>
                            <button type="button" onClick={() => saveEdit(item.id)} disabled={saving}>Guardar</button>
                            <button type="button" onClick={cancelEdit} disabled={saving}>Cancelar</button>
                          </>
                        ) : (
                          <>
                            <button type="button" onClick={() => handleOpen(item, false)}>Ver</button>
                            <button type="button" onClick={() => handleOpen(item, true)}>Bajar</button>
                            <button type="button" onClick={() => beginEdit(item)}>Editar</button>
                            <button
                              type="button"
                              onClick={() => replaceInputRefs.current[item.id]?.click()}
                              disabled={replacingId === item.id}
                            >
                              {replacingId === item.id ? 'Resubiendo...' : 'Resubir'}
                            </button>
                            <button type="button" className="danger" onClick={() => handleDelete(item)} disabled={saving}>
                              Eliminar
                            </button>
                            <input
                              ref={(element) => { replaceInputRefs.current[item.id] = element; }}
                              type="file"
                              className="doctor-sketches-panel__hidden-input"
                              onChange={(event) => handleReplace(item, event.target.files?.[0] || null)}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DoctorSketchesPanel;
